// src/prototype/components/EASourceBadge.tsx
// ============================================================
// EA SOURCE BADGE
// Shows the origin of a context attribute: Survey, Document, or Inferred
// ============================================================

import React from 'react';
import { ClipboardList, FileText, Sparkles } from 'lucide-react';

export type SourceType = 'survey' | 'document' | 'inferred';

interface EASourceBadgeProps {
  source: SourceType;
  documentName?: string; // Optional: show specific doc name on hover
  className?: string;
}

const sourceConfig: Record<SourceType, {
  label: string;
  icon: React.ElementType;
  bgColor: string;
  textColor: string;
  borderColor: string;
}> = {
  survey: {
    label: 'Survey',
    icon: ClipboardList,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
  },
  document: {
    label: 'Document',
    icon: FileText,
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200',
  },
  inferred: {
    label: 'Inferred',
    icon: Sparkles,
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
  },
};

export function EASourceBadge({ source, documentName, className = '' }: EASourceBadgeProps) {
  const config = sourceConfig[source];
  const Icon = config.icon;

  return (
    <span
      className={`
        inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold
        border ${config.bgColor} ${config.textColor} ${config.borderColor}
        ${className}
      `}
      title={source === 'document' && documentName ? `Source: ${documentName}` : undefined}
    >
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

export default EASourceBadge;